import React from 'react';
import { useLocation } from 'react-router-dom';
import ProductPage from '../components/seo/ProductPage';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/canaria' },
  { hreflang: 'en', href: '/en/canaria' },
  { hreflang: 'x-default', href: '/canaria' },
];

const CONTENT = {
  es: {
    seoTitle: 'Canaria+ | El golpe de calor se ve venir | Valoriza',
    seoDescription: 'Pulsera patentada con sensor de flujo térmico. Estima la temperatura corporal y alerta al operario antes del golpe de calor. Primer despliegue a gran escala en España.',
    breadcrumbLabel: 'Canaria+',
    tag: 'Wearable patentado',
    titleMain: 'Canaria+: el golpe de calor se ve',
    titleAccent: 'venir',
    lead: 'Pulsera patentada con sensor de flujo térmico. Estima la temperatura corporal y alerta al operario antes del golpe de calor. Primer despliegue a gran escala en España. En producción tras piloto 2025.',
    problema: 'El estrés térmico y los golpes de calor son un riesgo grave para los trabajadores expuestos a altas temperaturas, especialmente en sectores como la limpieza urbana o la construcción. La detección tardía de los síntomas puede tener consecuencias severas, incluyendo daños permanentes o fatales.',
    comoFunciona: 'Canaria+ es una pulsera patentada equipada con un sensor de flujo térmico avanzado. Monitoriza de forma continua la temperatura corporal del usuario, basándose en la medición de la disipación de calor. Cuando la temperatura alcanza niveles de riesgo, la pulsera emite una alerta temprana al operario y al sistema centralizado, permitiendo la intervención antes de que se produzca un golpe de calor.',
    cifras: [{ value: '1.º', label: 'Despliegue a gran escala en España' }, { value: 'Producción', label: 'Tras piloto 2025' }],
    paraQuien: 'Empresas con trabajadores expuestos a altas temperaturas, especialmente en trabajos al aire libre o en ambientes calurosos (limpieza, construcción, agricultura, obras públicas). Responsables de salud y seguridad laboral.',
    stack: ['Wearable IoT', 'Sensor de flujo térmico', 'Bluetooth', 'Biodatabank Cloud'],
    enlaces: [{ label: 'HALO-I: casco inteligente', to: '/halo-i' }, { label: 'Nuestra propiedad industrial', to: '/patentes' }, { label: 'Retos: reducción del riesgo físico', to: '/retos' }],
    ctaText: '¿Tienes un reto parecido?',
  },
  en: {
    seoTitle: 'Canaria+ | You can see heatstroke coming | Valoriza',
    seoDescription: 'Patented wristband with a heat-flux sensor. Estimates body temperature and alerts the worker before heatstroke hits. First large-scale rollout in Spain.',
    breadcrumbLabel: 'Canaria+',
    tag: 'Patented wearable',
    titleMain: 'Canaria+: you can see heatstroke',
    titleAccent: 'coming',
    lead: 'Patented wristband with a heat-flux sensor. Estimates body temperature and alerts the worker before heatstroke hits. First large-scale rollout in Spain. In production after the 2025 pilot.',
    problema: 'Heat stress and heatstroke are a serious risk for workers exposed to high temperatures, especially in sectors such as street cleaning or construction. Late detection of symptoms can have severe consequences, including permanent injury or death.',
    comoFunciona: 'Canaria+ is a patented wristband equipped with an advanced heat-flux sensor. It continuously monitors the wearer\u2019s body temperature by measuring heat dissipation. When the temperature reaches risk levels, the wristband issues an early alert to the worker and to the centralized system, allowing intervention before heatstroke occurs.',
    cifras: [{ value: '1st', label: 'Large-scale rollout in Spain' }, { value: 'Production', label: 'After the 2025 pilot' }],
    paraQuien: 'Companies with workers exposed to high temperatures, especially outdoor or hot-environment jobs (cleaning, construction, agriculture, public works). Occupational health and safety officers.',
    stack: ['IoT wearable', 'Heat-flux sensor', 'Bluetooth', 'Biodatabank Cloud'],
    enlaces: [{ label: 'HALO-I: smart helmet', to: '/en/halo-i' }, { label: 'Our industrial property', to: '/patentes' }, { label: 'Challenges: reducing physical risk', to: '/en/challenges' }],
    ctaText: 'Do you have a similar challenge?',
  },
};

export default function CanariaPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const c = CONTENT[locale];
  const path = locale === 'en' ? '/en/canaria' : '/canaria';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Canaria+',
    description: c.seoDescription,
    url: `https://innovation.valorizasm.com${path}`,
    brand: { '@id': 'https://innovation.valorizasm.com/#organization' },
  };

  return (
    <ProductPage
      testId="canaria-page"
      path={path}
      locale={locale}
      alternates={ALTERNATES}
      jsonLd={[jsonLd]}
      problemaLabel={locale === 'en' ? 'The problem' : 'El problema'}
      comoFuncionaLabel={locale === 'en' ? 'How it works' : 'Cómo funciona'}
      cifrasLabel={locale === 'en' ? 'Figures' : 'Cifras'}
      paraQuienLabel={locale === 'en' ? "Who it's for" : 'Para quién'}
      enlacesLabel={locale === 'en' ? 'Links' : 'Enlaces'}
      {...c}
    />
  );
}
